import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { VideoAssociation } from 'src/models/pilotage/packagePersoPilotageModel';

type LanguageCode = 'FR' | 'EN' | 'ES';

export interface EditVideoLanguageModalData {
  video: VideoAssociation;
  selectedLanguages: { [key: string]: boolean };
  allVideos: VideoAssociation[];
}

@Component({
    selector: 'app-edit-video-language-modal',
    imports: [FormsModule, MatIconModule, MatDialogModule, TranslateModule],
    templateUrl: './edit-video-language-modal.component.html',
    styleUrls: ['./edit-video-language-modal.component.scss']
})
export class EditVideoLanguageModalComponent {
  video: VideoAssociation;
  selectedLanguages: { [key: string]: boolean };
  allVideos: VideoAssociation[];

  editLanguageSelection: { [key: string]: boolean } = {
    FR: false,
    EN: false,
    ES: false
  };

  constructor(
    private dialogRef: MatDialogRef<EditVideoLanguageModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditVideoLanguageModalData
  ) {
    this.video = data.video;
    this.selectedLanguages = data.selectedLanguages;
    this.allVideos = data.allVideos;

    this.editLanguageSelection = {
      FR: this.video.languages.includes('FR'),
      EN: this.video.languages.includes('EN'),
      ES: this.video.languages.includes('ES')
    };
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const selectedLanguages = Object.entries(this.editLanguageSelection)
      .filter(([_, selected]) => selected)
      .map(([lang]) => lang as LanguageCode);

    this.dialogRef.close(selectedLanguages);
  }

  canDeselectLanguage(language: LanguageCode): boolean {
    const selectedCount = Object.values(this.editLanguageSelection).filter(selected => selected).length;

    if (this.editLanguageSelection[language] && selectedCount === 1) {
      return false;
    }

    return true;
  }

  onLanguageChange(language: LanguageCode): void {
    if (!this.canDeselectLanguage(language)) {
      this.editLanguageSelection[language] = true;
    }
  }

  hasAtLeastOneLanguageSelected(): boolean {
    return Object.values(this.editLanguageSelection).some(selected => selected);
  }

  isLanguageAvailableForEdit(language: LanguageCode): boolean {
    if (!this.video) return false;

    const isCurrentlyAssigned = this.video.languages.includes(language);
    const isAssignedToOtherVideo = this.allVideos
      .filter(v => v.id !== this.video.id)
      .some(v => v.languages.includes(language));

    return isCurrentlyAssigned || (!isAssignedToOtherVideo && this.selectedLanguages[language]);
  }
}